import React from 'react';
import {
  Alert,
  Bullseye,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  Spinner,
  TextInput,
  Title,
  ToggleGroup,
  ToggleGroupItem,
} from '@patternfly/react-core';
import { CountrySelect } from '../../../utils/CountrySelect';
import { MAX_REGISTRAR_PHONE_LENGTH, canonicalizeRegistrarPhone } from '../../../../utils/registrarPhone';
import type { ContactDraft, ContactMode } from './types';
import type { DomainName } from '../../../../types/models';

interface ContactStepProps {
  mode: ContactMode;
  onModeChange: (mode: ContactMode) => void;
  contact: ContactDraft;
  onFieldChange: (field: keyof ContactDraft, value: string) => void;
  errors: Record<string, string>;
  // Portfolio reuse
  domainNames: DomainName[];
  domainNamesLoading: boolean;
  selectedPortfolioId: number;
  onSelectPortfolio: (id: number) => void;
  portfolioContactLoading: boolean;
  /** True when the picked portfolio domain has no saved registrant contact. */
  portfolioContactMissing: boolean;
  /**
   * Hide the step's own "Registrant contact" heading + intro — for callers that render their own
   * heading above this form (the transfer wizard's keep/provide choice).
   */
  hideIntro?: boolean;
}

/** The id of a field's helper text — wired to its input via `aria-describedby`. */
const helperTextId = (fieldId: string) => `${fieldId}-helper`;

const FieldGroup: React.FunctionComponent<{
  label: string;
  fieldId: string;
  isRequired?: boolean;
  error?: string;
  hint?: string;
  /** Status styling for `hint` (an `error` always wins). Use `warning` for advisory, non-blocking copy. */
  hintVariant?: 'default' | 'warning';
  /**
   * Announce helper-text changes politely. Set it for a hint whose TEXT changes while the user types
   * (the phone preview) — PatternFly requires a live region for dynamically updated helper text.
   */
  isLiveRegion?: boolean;
  children: React.ReactNode;
}> = ({ label, fieldId, isRequired, error, hint, hintVariant = 'default', isLiveRegion, children }) => (
  <FormGroup label={label} isRequired={isRequired} fieldId={fieldId}>
    {children}
    {(error || hint) && (
      <FormHelperText>
        <HelperText isLiveRegion={isLiveRegion}>
          <HelperTextItem id={helperTextId(fieldId)} variant={error ? 'error' : hintVariant}>
            {error ?? hint}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    )}
  </FormGroup>
);

const ContactStep: React.FunctionComponent<ContactStepProps> = ({
  mode,
  onModeChange,
  contact,
  onFieldChange,
  errors,
  domainNames,
  domainNamesLoading,
  selectedPortfolioId,
  onSelectPortfolio,
  portfolioContactLoading,
  portfolioContactMissing,
  hideIntro = false,
}) => {
  const portfolioDomains = domainNames.filter((d) => d.id !== null);

  const textField = (
    field: keyof ContactDraft,
    label: string,
    options: {
      isRequired?: boolean;
      hint?: string;
      hintVariant?: 'default' | 'warning';
      isLiveRegion?: boolean;
      type?: 'text' | 'email' | 'tel';
      maxLength?: number;
      onBlur?: () => void;
    } = {},
  ) => (
    <FieldGroup
      label={label}
      fieldId={`contact-${field}`}
      isRequired={options.isRequired}
      error={errors[field]}
      hint={options.hint}
      hintVariant={options.hintVariant}
      isLiveRegion={options.isLiveRegion}
    >
      <TextInput
        id={`contact-${field}`}
        type={options.type ?? 'text'}
        value={contact[field]}
        onChange={(_event, value) => onFieldChange(field, value)}
        onBlur={options.onBlur}
        maxLength={options.maxLength}
        isRequired={options.isRequired}
        validated={errors[field] ? 'error' : 'default'}
        aria-describedby={errors[field] || options.hint ? helperTextId(`contact-${field}`) : undefined}
      />
    </FieldGroup>
  );

  // --- Registrant phone: canonicalise to the one spelling registrars accept ---------------------
  // The backend rewrites `phone` to `+<calling code>.<subscriber>` on every write, so the form shows
  // that value up front (live preview, then a rewrite on blur) instead of letting the field diverge
  // from what gets stored. Advisory only: a number this parser rejects is still submitted, and the
  // backend's own `data.phone` message is the authority.
  const [phoneBlurred, setPhoneBlurred] = React.useState(false);
  const phoneResult = React.useMemo(
    () => canonicalizeRegistrarPhone(contact.phone, contact.countrycode),
    [contact.phone, contact.countrycode],
  );

  let phoneHint = 'Type it any way you like — it is saved as +CC.number, e.g. +32.473999803.';
  let phoneHintVariant: 'default' | 'warning' = 'default';
  if (phoneResult.ok) {
    phoneHint = `Saved as ${phoneResult.value}`;
  } else if (phoneBlurred && phoneResult.reason !== 'required') {
    // Hold the complaint until the field has been left once — warning on every keystroke of a
    // half-typed number nags the user for input that is simply unfinished.
    phoneHint = phoneResult.message;
    phoneHintVariant = 'warning';
  }

  const handlePhoneBlur = () => {
    setPhoneBlurred(true);
    if (phoneResult.ok && phoneResult.value !== contact.phone) {
      onFieldChange('phone', phoneResult.value);
    }
  };

  const handleCountryChange = (value: string) => {
    onFieldChange('countrycode', value);
    // A national number (`0473 …`) only becomes parseable once the country is known — and one typed
    // for another country must be re-read against the new one. Re-canonicalise with the NEW value in
    // the same interaction, so picking the country is what makes the phone snap into shape.
    const withNewCountry = canonicalizeRegistrarPhone(contact.phone, value);
    if (withNewCountry.ok && withNewCountry.value !== contact.phone) {
      onFieldChange('phone', withNewCountry.value);
    }
  };

  return (
    <Form onSubmit={(event) => event.preventDefault()}>
      {!hideIntro && (
        <>
          <Title headingLevel="h2" size="lg">
            Registrant contact
          </Title>
          <Content component="p">
            This is the owner contact registered with the registrar for every domain in this order.
          </Content>
        </>
      )}

      <FormGroup label="Contact source" fieldId="contact-mode">
        <ToggleGroup aria-label="Choose how to provide the registrant contact">
          <ToggleGroupItem
            text="Enter a new contact"
            buttonId="contact-mode-new"
            isSelected={mode === 'new'}
            onChange={() => onModeChange('new')}
          />
          <ToggleGroupItem
            text="Use a contact from my domains"
            buttonId="contact-mode-portfolio"
            isSelected={mode === 'portfolio'}
            onChange={() => onModeChange('portfolio')}
          />
        </ToggleGroup>
      </FormGroup>

      {mode === 'portfolio' && (
        <FormGroup label="Copy contact from domain" fieldId="contact-portfolio">
          {domainNamesLoading ? (
            <Bullseye>
              <Spinner size="md" aria-label="Loading your domains" />
            </Bullseye>
          ) : portfolioDomains.length === 0 ? (
            <HelperText>
              <HelperTextItem>
                You have no other domains to copy a contact from. Enter a new contact instead.
              </HelperTextItem>
            </HelperText>
          ) : (
            <>
              <FormSelect
                id="contact-portfolio"
                value={selectedPortfolioId > 0 ? String(selectedPortfolioId) : ''}
                onChange={(_event, value) => onSelectPortfolio(Number(value) || 0)}
                aria-label="Select a domain to copy its registrant contact"
              >
                <FormSelectOption value="" label="Select one of your domains" isPlaceholder />
                {portfolioDomains.map((d) => (
                  <FormSelectOption
                    key={d.id as number}
                    value={String(d.id)}
                    label={
                      d.registrant_contact
                        ? `${d.name} — ${d.registrant_contact.first_name} ${d.registrant_contact.last_name}`
                        : d.name
                    }
                  />
                ))}
              </FormSelect>
              {portfolioContactLoading && (
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem icon={<Spinner size="sm" />}>Loading the contact…</HelperTextItem>
                  </HelperText>
                </FormHelperText>
              )}
              {portfolioContactMissing && !portfolioContactLoading && (
                <Alert
                  variant="info"
                  isInline
                  isPlain
                  title="That domain has no saved contact — fill the fields below."
                  className="pf-v6-u-mt-sm"
                />
              )}
            </>
          )}
        </FormGroup>
      )}

      <Grid hasGutter>
        <GridItem md={6}>{textField('first_name', 'First name', { isRequired: true })}</GridItem>
        <GridItem md={6}>{textField('last_name', 'Last name', { isRequired: true })}</GridItem>
        <GridItem md={12}>{textField('organization', 'Organization')}</GridItem>
        <GridItem md={6}>{textField('email', 'Email', { isRequired: true, type: 'email' })}</GridItem>
        <GridItem md={6}>
          {textField('phone', 'Phone', {
            isRequired: true,
            type: 'tel',
            hint: phoneHint,
            hintVariant: phoneHintVariant,
            isLiveRegion: true,
            // The column cap, checked by the backend on the RAW value — never set it lower.
            maxLength: MAX_REGISTRAR_PHONE_LENGTH,
            onBlur: handlePhoneBlur,
          })}
        </GridItem>
        <GridItem md={12}>{textField('address_line1', 'Address line 1', { isRequired: true })}</GridItem>
        <GridItem md={12}>{textField('address_line2', 'Address line 2')}</GridItem>
        <GridItem md={6}>{textField('city', 'City', { isRequired: true })}</GridItem>
        <GridItem md={6}>
          {textField('state', 'State / province', {
            isRequired: true,
            // The registrar only accepts its own province vocabulary — for Belgium the ASCII name or
            // the ISO 3166-2 code without prefix; accented/localized forms are rejected at purchase.
            hint: 'Use the registrar spelling — for Belgium e.g. Liege or WLG (never Liège).',
          })}
        </GridItem>
        <GridItem md={6}>{textField('zipcode', 'Postal code', { isRequired: true })}</GridItem>
        <GridItem md={6}>
          <FieldGroup label="Country" fieldId="contact-countrycode" isRequired error={errors.countrycode}>
            <CountrySelect value={contact.countrycode} onChange={handleCountryChange} />
          </FieldGroup>
        </GridItem>
      </Grid>
    </Form>
  );
};

export default ContactStep;
